import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Loader2,
  RotateCcw,
  Trash2,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

import {
  AttributeReviewCompleteState,
  AttributeReviewContextHeader,
  AttributeReviewEmptyState,
  AttributeReviewFilters,
  ApprovalPipelineActivityPanel,
  ReviewQueue,
  ApprovalSyncFailedBanner,
  DeleteMappingSessionConfirm,
  PastAnswersButton,
  ContextBar,
  LogViewer,
} from "@/components/catalog";
import { DashboardLayout } from "@/components/layout";
import { Button, ConfirmationModal, PageHeader } from "@/components/ui";
import { useSidebar } from "@/contexts/SidebarContext";
import { useFoldMenuWhileOpen } from "@/hooks/useFoldMenuWhileOpen";
import {
  useTaxonomyIngestionRunQuery,
  useTaxonomyIngestionRunsQuery,
} from "@/graphql/generated";
import { useApplyPastAnswers } from "@/hooks/useApplyPastAnswers";
import { useAttributeReview } from "@/hooks/useAttributeReview";
import { useSubscriptionConnectionState } from "@/hooks/useSubscriptionConnectionState";
import { useTaxonomyIngestionSessionSubscription } from "@/hooks/useTaxonomyIngestionSessionSubscription";
import { withAppBase } from "@/lib/appPaths";
import { formatProductType } from "@/lib/formatProductType";
import { isApprovedAttributeReviewStatus } from "@/lib/attributeReview/reviewQueueStatus";
import {
  getApprovalPipelineFailureMessage,
  getApprovalPipelineStatusLabel,
  isApprovalPipelineActive,
  normalizeApprovalPipelineStatus,
  pipelineUpdateHasTerminalFailure,
  resultMessage,
} from "@/lib/attributeReview/approvalPipeline";
import { cn } from "@/lib/utils";
import { ReviewQueueSkeleton } from "@/components/catalog/ReviewQueue/ReviewQueueSkeleton";
import { ReviewDrawer } from "@/components/catalog/ReviewDrawer";
import { PAGE_BESIDE_DRAWER } from "@/components/catalog/ReviewDrawer/drawerWidth";
import { useReviewDrawerRow } from "@/hooks/useReviewDrawerRow";
import { saveAllConfirmText } from "@/lib/attributeReview/saveAllCounts";
import { pathDecision } from "@/lib/attributeReview/pathDecision";
import { fieldLabelOf } from "@/lib/attributeReview/fieldLabels";
import { sameLeafLabels } from "@/lib/attributeReview/reviewGroups";
import { useEscapeToClose } from "@/hooks/useEscapeToClose";
import { useFollowRow } from "@/hooks/useFollowRow";
import { useReviewOrder } from "@/hooks/useReviewOrder";
import {
  FinishedEditBanner,
  FinishedEditConfirmDialog,
} from "@/components/catalog/FinishedEdit";
import { firstFailedMessage } from "@/lib/attributeReview/firstFailedMessage";

type ActivityLogEntry = {
  id: string;
  timestamp: string;
  level: string;
  message: string;
};

function formatElapsedDuration(
  startedAt?: string | null,
  endedAt?: string | null,
): string {
  if (!startedAt) return "Just started";
  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  const diffMs = Math.max(0, end - start);
  const totalSeconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
  }
  return `${seconds}s`;
}

// Prefer the readable displayName only for opaque codes (Meesho/Coupang numeric
// ids that formatProductType can't improve); other marketplaces keep their raw
// productType label exactly as before.
function ingestionRunLabel(run: {
  productType: string;
  displayName?: string | null;
}): string {
  return /^\d+$/.test(run.productType.trim()) &&
    formatProductType(run.productType) === run.productType
    ? run.displayName || run.productType
    : run.productType;
}

function toActivityLogEntry(params: {
  id: string;
  timestamp: string;
  status?: string | null;
  message?: string | null;
}): ActivityLogEntry {
  const status = (params.status ?? "").toUpperCase();
  const level =
    status === "FAILED"
      ? "error"
      : status === "REVIEW_READY" || status === "COMPLETED"
        ? "success"
        : "info";

  return {
    id: params.id,
    timestamp: params.timestamp,
    level,
    message: params.message ?? "Progress updated",
  };
}

export default function AttributeReview() {
  const { collapsed } = useSidebar();
  const [confirmAction, setConfirmAction] = useState<
    "COMPLETE" | "DELETE" | null
  >(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activityMarketplaceFilter, setActivityMarketplaceFilter] =
    useState<string>("ALL");
  const [activityStatusFilter, setActivityStatusFilter] =
    useState<string>("ACTIVE");
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [activityLogs, setActivityLogs] = useState<ActivityLogEntry[]>([]);
  const seenActivityEventIdsRef = useRef(new Set<string>());
  const previousConnectionStateRef = useRef<string>("idle");

  const connectionState = useSubscriptionConnectionState();
  const ingestionRunsVariables = useMemo(
    () => ({
      marketplace:
        activityMarketplaceFilter === "ALL"
          ? undefined
          : activityMarketplaceFilter,
      statuses:
        activityStatusFilter === "ACTIVE" || activityStatusFilter === "ALL"
          ? undefined
          : [activityStatusFilter],
      includeCompleted: activityStatusFilter !== "ACTIVE",
      limit: 25,
    }),
    [activityMarketplaceFilter, activityStatusFilter],
  );

  const {
    data: ingestionRunsData,
    isLoading: ingestionRunsLoading,
    refetch: refetchIngestionRuns,
  } = useTaxonomyIngestionRunsQuery(ingestionRunsVariables, {
    refetchOnWindowFocus: false,
  });
  const ingestionRuns = useMemo(
    () => ingestionRunsData?.taxonomyIngestionRuns ?? [],
    [ingestionRunsData?.taxonomyIngestionRuns],
  );
  const hasIngestionRuns = ingestionRuns.length > 0;

  const { data: selectedRunData, refetch: refetchSelectedRun } =
    useTaxonomyIngestionRunQuery(
      { runId: selectedRunId ?? "" },
      {
        enabled: !!selectedRunId,
        refetchOnWindowFocus: false,
      },
    );
  const selectedRun = selectedRunData?.taxonomyIngestionRun ?? null;

  const {
    sessionsLoading,
    sessionsUnderReview,
    currentSessionId,
    isCurrentSessionApprovalSyncFailed,
    approvalSyncError,
    approvalSyncFieldErrors,
    marketplaces,
    productTypes,
    selectedMarketplace,
    selectedProductType,
    setSelectedMarketplace,
    setSelectedProductType,
    context,
    mappings,
    matchedMappings,
    hasFieldMappingsPayload,
    fieldMappingsLoading,
    fieldMappingsEmpty,
    fieldMappingsError,
    needsReview,
    saveAllBlockers,
    saveAllReady,
    saveAllHidden,
    lowConfidence,
    reviewSummaryFilter,
    selectedIds,
    someSelected,
    draftReviewSelections,
    setReviewSummaryFilter,
    handleSelectChange,
    setSelectedIds,
    expandedId,
    openRow,
    approveRows,
    ignoreRows,
    decideRows,
    handleDraftReviewChange,
    approvalPipeline,
    approvalQueuedFieldIds,
    activeApprovalPipelines,
    retryApprovalPipeline,
    unrejectFieldMapping,
    completeReview,
    resumeFailedApprovalSync,
    canResumeFailedApprovalSync,
    isResumingFailedApprovalSync,
    handleDeleteSession,
    isDeletingSession,
    isSaving,
    editingFinished,
    exitFinishedEdit,
    finishedEdit,
    finishedEditDialog,
  } = useAttributeReview();
  const { applyPastAnswers, isApplying: isApplyingPastAnswers } =
    useApplyPastAnswers();

  const runMarketplaceOptions = useMemo(() => {
    return Array.from(
      new Set(ingestionRuns.map((run) => run.marketplace).filter(Boolean)),
    ).sort();
  }, [ingestionRuns]);

  useEffect(() => {
    if (selectedRunId) return;
    if (ingestionRuns.length === 0) return;
    setSelectedRunId(ingestionRuns[0].runId);
  }, [ingestionRuns, selectedRunId]);

  useEffect(() => {
    if (ingestionRuns.length > 0) return;
    setSelectedRunId(null);
  }, [ingestionRuns]);

  useEffect(() => {
    seenActivityEventIdsRef.current.clear();
    setActivityLogs([]);
  }, [selectedRunId]);

  useEffect(() => {
    if (!selectedRunId || !selectedRun) return;
    setActivityLogs((prev) =>
      prev.length > 0
        ? prev
        : [
            toActivityLogEntry({
              id: `snapshot-${selectedRun.runId}-${selectedRun.updatedAt}`,
              timestamp: selectedRun.updatedAt,
              status: selectedRun.status,
              message: selectedRun.userMessage ?? selectedRun.stageLabel,
            }),
          ],
    );
  }, [selectedRun, selectedRunId]);

  useEffect(() => {
    const previous = previousConnectionStateRef.current;
    if (
      selectedRunId &&
      previous !== "connected" &&
      connectionState === "connected"
    ) {
      void refetchSelectedRun();
    }
    previousConnectionStateRef.current = connectionState;
  }, [connectionState, refetchSelectedRun, selectedRunId]);

  useTaxonomyIngestionSessionSubscription({
    runId: selectedRunId,
    enabled: !!selectedRunId,
    onEvent: (event) => {
      if (seenActivityEventIdsRef.current.has(event.eventId)) return;
      seenActivityEventIdsRef.current.add(event.eventId);
      setActivityLogs((prev) => [
        ...prev,
        toActivityLogEntry({
          id: event.eventId,
          timestamp: event.occurredAt,
          status: event.status,
          message: event.userMessage ?? event.stageLabel,
        }),
      ]);
      void refetchSelectedRun();
      if (event.terminal) {
        void refetchIngestionRuns();
      }
    },
    onError: () => {
      void refetchSelectedRun();
    },
  });

  const hasSessions =
    !sessionsLoading &&
    (sessionsUnderReview.length > 0 || Boolean(currentSessionId));
  const needsReviewMappings = mappings;
  const [onlyOpen, setOnlyOpen] = useState(false);
  const showOnlyOpen = onlyOpen && mappings.length > 0;
  const openIds = useMemo(
    () => new Set(mappings.map((row) => String(row.id))),
    [mappings],
  );
  const queueRows = useMemo(
    () => [...mappings, ...matchedMappings],
    [mappings, matchedMappings],
  );
  const showTable =
    hasSessions && (mappings.length > 0 || matchedMappings.length > 0);
  const showCompleteState =
    hasSessions &&
    hasFieldMappingsPayload &&
    !fieldMappingsLoading &&
    !fieldMappingsError &&
    mappings.length === 0 &&
    matchedMappings.length === 0;
  const handleBackToCatalog = () => {
    window.location.href = withAppBase("/catalog");
  };

  const handleOpenReviewFromRun = () => {
    if (!selectedRun?.mappingSessionId) return;
    const next = new URLSearchParams(searchParams);
    next.set("sessionId", selectedRun.mappingSessionId);
    setSearchParams(next);
    document
      .getElementById("attribute-review-section")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const approvalPipelineActive = isApprovalPipelineActive(approvalPipeline);
  const approvalPipelineStatus =
    approvalPipeline?.status != null
      ? normalizeApprovalPipelineStatus(approvalPipeline.status)
      : null;
  const approvalPipelineLabel = approvalPipeline
    ? getApprovalPipelineStatusLabel(approvalPipeline.status)
    : null;
  const approvalPipelineRowMessage =
    approvalPipeline?.fieldResults.map(resultMessage).find(Boolean) ??
    approvalPipeline?.valueResults.map(resultMessage).find(Boolean) ??
    null;
  const approvalPipelineMessage = approvalPipeline
    ? pipelineUpdateHasTerminalFailure(approvalPipeline)
      ? (firstFailedMessage(
          approvalPipeline.fieldResults,
          approvalPipeline.valueResults,
        ) ?? getApprovalPipelineFailureMessage(approvalPipeline))
      : (approvalPipeline.message ?? approvalPipelineRowMessage)
    : null;

  const { closed, toggleGroup, setAllGroups, orderedIds, settledIds } =
    useReviewOrder(queueRows, approvalQueuedFieldIds);
  const drawer = useReviewDrawerRow(
    queueRows,
    orderedIds,
    settledIds,
    expandedId,
    openRow,
  );
  useFoldMenuWhileOpen(Boolean(drawer.row));
  const cursorId = useFollowRow(orderedIds, expandedId);
  useEscapeToClose(Boolean(drawer.row), () => openRow(null));

  return (
    <DashboardLayout
      activePath="/catalog/mapping"
      contentClassName={cn(
        "mx-0 max-w-none p-4 md:p-5",
        drawer.row && PAGE_BESIDE_DRAWER,
      )}
    >
      <PageHeader
        title="Attribute Mapping Review"
        subtitle="Review and approve ML-suggested attribute mappings"
        breadcrumbs={[
          { label: "Catalog", active: false },
          { label: "Mapping Review", active: true },
        ]}
        actions={
          <div className="flex items-center gap-2">
            {hasFieldMappingsPayload &&
              saveAllBlockers === 0 &&
              !finishedEdit && (
                <Button
                  variant="primary"
                  onClick={() => setConfirmAction("COMPLETE")}
                  // saveAllBlockers can hit 0 while an approval is still QUEUED/RUNNING,
                  // and pinned rows outlive a terminal pipeline until their
                  // refetch confirms — block Save all through both windows.
                  disabled={
                    approvalPipelineActive ||
                    isSaving ||
                    approvalQueuedFieldIds.size > 0
                  }
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Save all
                </Button>
              )}
            <Button asChild variant="outline">
              <a href={withAppBase("/catalog/mapping/jobs")}>
                <Clock3 className="h-4 w-4" />
                Approval Jobs
              </a>
            </Button>
          </div>
        }
      />

      <ContextBar />

      {hasIngestionRuns && (
        <div className="mb-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
          <div className="flex flex-col gap-4 border-b border-slate-200 dark:border-slate-800 px-5 py-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Ingestion Activity
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Monitor active mapping ingestions and rejoin one after refresh.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <select
                value={activityMarketplaceFilter}
                onChange={(e) => {
                  setActivityMarketplaceFilter(e.target.value);
                  setSelectedRunId(null);
                }}
                className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-700 dark:text-slate-200"
              >
                <option value="ALL">All marketplaces</option>
                {runMarketplaceOptions.map((marketplace) => (
                  <option key={marketplace} value={marketplace}>
                    {marketplace}
                  </option>
                ))}
              </select>

              <select
                value={activityStatusFilter}
                onChange={(e) => {
                  setActivityStatusFilter(e.target.value);
                  setSelectedRunId(null);
                }}
                className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-700 dark:text-slate-200"
              >
                <option value="ACTIVE">Active only</option>
                <option value="ALL">All statuses</option>
                <option value="QUEUED">Queued</option>
                <option value="FETCHING_SCHEMA">Checking requirements</option>
                <option value="PREPARING_INPUT">Preparing details</option>
                <option value="MATCHING_ATTRIBUTES">Matching fields</option>
                <option value="REVIEW_READY">Ready for review</option>
                <option value="FAILED">Failed</option>
              </select>
            </div>
          </div>

          <div className="grid gap-0 lg:grid-cols-[320px_minmax(0,1fr)]">
            <div className="border-r border-slate-200 dark:border-slate-800">
              <div className="max-h-[420px] overflow-y-auto">
                {ingestionRunsLoading ? (
                  <div className="flex items-center gap-2 px-5 py-6 text-sm text-slate-500 dark:text-slate-400">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading active ingestions...
                  </div>
                ) : (
                  ingestionRuns.map((run) => {
                    const isSelected = selectedRunId === run.runId;
                    return (
                      <button
                        key={run.runId}
                        type="button"
                        onClick={() => setSelectedRunId(run.runId)}
                        className={`w-full border-b border-slate-100 px-5 py-4 text-left transition-colors dark:border-slate-800 ${
                          isSelected
                            ? "bg-indigo-50 dark:bg-indigo-950/30"
                            : "hover:bg-slate-50 dark:hover:bg-slate-950/50"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                              {ingestionRunLabel(run)}
                            </p>
                            <p className="mt-1 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                              {run.marketplace}
                            </p>
                          </div>
                          <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:text-slate-300">
                            {Math.round(run.progressPercent ?? 0)}%
                          </span>
                        </div>
                        <p className="mt-2 line-clamp-2 text-sm text-slate-600 dark:text-slate-300">
                          {run.userMessage ?? run.stageLabel ?? "In progress"}
                        </p>
                        <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
                          Updated {formatElapsedDuration(run.updatedAt, null)}{" "}
                          ago
                        </p>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="p-5">
              {!selectedRun ? (
                <div className="flex min-h-[320px] items-center justify-center rounded-xl border border-dashed border-slate-200 dark:border-slate-800 text-sm text-slate-500 dark:text-slate-400">
                  Select an ingestion to see its status and live updates.
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-col gap-4 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                            {ingestionRunLabel(selectedRun)}
                          </h3>
                          <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:text-slate-300">
                            {selectedRun.marketplace}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                          {selectedRun.userMessage ?? selectedRun.stageLabel}
                        </p>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                            connectionState === "connected"
                              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"
                              : connectionState === "connecting"
                                ? "bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300"
                                : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                          }`}
                        >
                          {connectionState === "connected" ? (
                            <Wifi className="h-3.5 w-3.5" />
                          ) : (
                            <WifiOff className="h-3.5 w-3.5" />
                          )}
                          {connectionState === "connected"
                            ? "Live"
                            : connectionState === "connecting"
                              ? "Reconnecting"
                              : "Offline"}
                        </span>

                        {selectedRun.mappingSessionId && (
                          <Button size="sm" onClick={handleOpenReviewFromRun}>
                            <CheckCircle2 className="h-4 w-4" />
                            Open Review
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-3">
                      <div className="rounded-lg bg-slate-50 dark:bg-slate-950/50 p-3">
                        <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Progress
                        </p>
                        <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-white">
                          {Math.round(selectedRun.progressPercent ?? 0)}%
                        </p>
                        <div className="mt-3 h-2 rounded-full bg-slate-200 dark:bg-slate-800">
                          <div
                            className="h-2 rounded-full bg-indigo-500 transition-all"
                            style={{
                              width: `${Math.max(
                                6,
                                Math.min(100, selectedRun.progressPercent ?? 0),
                              )}%`,
                            }}
                          />
                        </div>
                      </div>

                      <div className="rounded-lg bg-slate-50 dark:bg-slate-950/50 p-3">
                        <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Time Spent
                        </p>
                        <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-white">
                          {formatElapsedDuration(
                            selectedRun.startedAt,
                            selectedRun.completedAt ?? undefined,
                          )}
                        </p>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                          Started{" "}
                          {new Date(selectedRun.startedAt).toLocaleString()}
                        </p>
                      </div>

                      <div className="rounded-lg bg-slate-50 dark:bg-slate-950/50 p-3">
                        <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          What&apos;s Next
                        </p>
                        <p className="mt-2 text-sm font-medium text-slate-900 dark:text-white">
                          {selectedRun.approxRemainingLabel ??
                            (selectedRun.terminal
                              ? "This run is no longer streaming updates."
                              : "Waiting for the next stage update.")}
                        </p>
                        {selectedRun.mappingSessionId && (
                          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                            Review session: {selectedRun.mappingSessionId}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <LogViewer logs={activityLogs} maxHeight="260px" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        id="attribute-review-section"
        className={cn(
          "grid gap-5",
          !drawer.row && "2xl:grid-cols-[256px_minmax(0,1fr)]",
        )}
      >
        <div className={cn("min-w-0", drawer.row && "hidden")}>
          <AttributeReviewFilters
            marketplaces={marketplaces}
            productTypes={productTypes}
            selectedMarketplace={selectedMarketplace}
            selectedProductType={selectedProductType}
            onMarketplaceSelect={setSelectedMarketplace}
            onProductTypeSelect={setSelectedProductType}
            context={context}
            needsReview={needsReview}
            sessionsLoading={sessionsLoading || fieldMappingsLoading}
            finishedSession={editingFinished}
            onExitFinishedSession={exitFinishedEdit}
          />
        </div>

        <div className="min-w-0">
          {sessionsLoading ? (
            <div className="flex flex-col items-center justify-center h-[500px] p-12 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg">
              <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                Loading review sessions...
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-md">
                Please wait while we fetch the latest attribute mapping
                suggestions.
              </p>
            </div>
          ) : !hasSessions ? (
            <AttributeReviewEmptyState onBackToCatalog={handleBackToCatalog} />
          ) : (
            <>
              <AttributeReviewContextHeader
                context={context}
                needDecisions={needsReviewMappings.length}
                onlyOpen={showOnlyOpen}
                onToggleOnlyOpen={() => setOnlyOpen(!showOnlyOpen)}
                lowConfidence={lowConfidence}
                activeSummaryFilter={reviewSummaryFilter}
                onSummaryFilterChange={setReviewSummaryFilter}
              />
              {finishedEdit && <FinishedEditBanner edit={finishedEdit} />}

              {approvalPipeline &&
                pipelineUpdateHasTerminalFailure(approvalPipeline) && (
                  <div
                    className={cn(
                      "mb-4 flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between",
                      approvalPipelineStatus === "FAILED" ||
                        approvalPipelineStatus === "CANCELLED"
                        ? "border-red-200 bg-red-50 text-red-900 dark:border-red-900/70 dark:bg-red-950/30 dark:text-red-100"
                        : approvalPipelineStatus === "SUCCESS"
                          ? "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/70 dark:bg-emerald-950/30 dark:text-emerald-100"
                          : "border-indigo-200 bg-indigo-50 text-indigo-900 dark:border-indigo-900/70 dark:bg-indigo-950/30 dark:text-indigo-100",
                    )}
                  >
                    <div className="flex items-start gap-3">
                      {approvalPipelineActive ? (
                        <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin" />
                      ) : approvalPipelineStatus === "SUCCESS" ? (
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                      ) : (
                        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                      )}
                      <div>
                        <p className="text-sm font-semibold">
                          {approvalPipelineLabel}
                        </p>
                        <p className="mt-1 text-sm">
                          {approvalPipelineMessage ??
                            (approvalPipelineActive
                              ? "Selected mappings are moving through the approval pipeline."
                              : "Approval pipeline update received.")}
                        </p>
                        {approvalPipeline.syncJobId && (
                          <p className="mt-1 text-xs opacity-75">
                            Job {approvalPipeline.syncJobId}
                          </p>
                        )}
                      </div>
                    </div>
                    {pipelineUpdateHasTerminalFailure(approvalPipeline) &&
                      approvalPipeline.retryable && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            void retryApprovalPipeline();
                          }}
                          disabled={isSaving}
                          className="shrink-0 bg-white/80"
                        >
                          {isSaving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RotateCcw className="h-4 w-4" />
                          )}
                          Retry Approval
                        </Button>
                      )}
                  </div>
                )}

              {isCurrentSessionApprovalSyncFailed && (
                <ApprovalSyncFailedBanner
                  fieldErrors={approvalSyncFieldErrors}
                  message={approvalSyncError}
                  rows={mappings}
                  canResume={canResumeFailedApprovalSync}
                  isResuming={isResumingFailedApprovalSync}
                  isSaving={isSaving}
                  onFix={openRow}
                  onResume={() => void resumeFailedApprovalSync()}
                />
              )}

              {currentSessionId && !editingFinished && !finishedEdit && (
                <div className="mb-4 flex justify-end gap-2">
                  <PastAnswersButton
                    busy={isApplyingPastAnswers}
                    onClick={() => applyPastAnswers(currentSessionId)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setConfirmAction("DELETE")}
                    disabled={isDeletingSession}
                    className="shrink-0 border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-950/40"
                  >
                    {isDeletingSession ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                    {isDeletingSession ? "Deleting..." : "Delete mapping"}
                  </Button>
                </div>
              )}

              {fieldMappingsEmpty ? (
                <div className="flex flex-col items-center justify-center p-12 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg">
                  <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-full flex items-center justify-center mb-4">
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                    No fields to review in this session
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 text-center max-w-md">
                    This session has no field mappings — typically a session
                    reopened after approval already consumed its review data.
                    Re-run the product type sync, or delete this session.
                  </p>
                  <div className="mt-4 flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-950/40"
                      onClick={() => setConfirmAction("DELETE")}
                      disabled={isDeletingSession}
                    >
                      {isDeletingSession ? "Deleting..." : "Delete mapping"}
                    </Button>
                  </div>
                </div>
              ) : fieldMappingsLoading ? (
                <ReviewQueueSkeleton />
              ) : fieldMappingsError ? (
                <div className="flex flex-col items-center justify-center p-12 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg">
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mb-4">
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                    Failed to load field mappings
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 text-center max-w-md">
                    There was a problem loading the mappings for this session.
                    Please try refreshing the page or checking your network
                    connection.
                  </p>
                </div>
              ) : showTable ? (
                <ReviewQueue
                  rows={showOnlyOpen ? mappings : queueRows}
                  variant={drawer.row ? "compact" : "table"}
                  cursorId={cursorId}
                  closed={closed}
                  onToggleGroup={toggleGroup}
                  onSetAllGroups={setAllGroups}
                  queuedIds={approvalQueuedFieldIds}
                  openIds={openIds}
                  drafts={draftReviewSelections}
                  selectedIds={selectedIds}
                  onSelectChange={handleSelectChange}
                  onClearSelection={() => setSelectedIds(new Set())}
                  onApprove={approveRows}
                  onIgnore={ignoreRows}
                  onUndo={unrejectFieldMapping}
                  onShowToSeller={(row) =>
                    decideRows([pathDecision("showToSeller", row)])
                  }
                  onOpen={openRow}
                />
              ) : showCompleteState ? (
                <AttributeReviewCompleteState
                  onBackToCatalog={handleBackToCatalog}
                />
              ) : null}
            </>
          )}
        </div>
      </div>

      <ReviewDrawer
        current={
          drawer.row
            ? {
                row: drawer.row,
                sessionId: String(currentSessionId ?? ""),
                draft: draftReviewSelections[String(drawer.row.id)],
                positionText: drawer.positionText,
                isApproving: approvalQueuedFieldIds.has(String(drawer.row.id)),
                hasPrev: Boolean(drawer.prevId),
                hasNext: Boolean(drawer.nextId),
                onPrev: () => openRow(drawer.prevId),
                onNext: () => openRow(drawer.nextId),
                onClose: () => openRow(null),
                afterSettleId: drawer.afterSettleId,
                onOpenRow: openRow,
                onDraftChange: handleDraftReviewChange,
                approveRows,
                ignoreRows,
                decideRows,
                marketplace: context.sourceMarketplace,
                productType: context.productType,
                sameLeafLabel: sameLeafLabels(queueRows).get(
                  String(drawer.row.id),
                ),
                fieldLabelOf: fieldLabelOf(queueRows),
                onUnreject:
                  finishedEdit &&
                  isApprovedAttributeReviewStatus(drawer.row.decisionStatus)
                    ? undefined
                    : unrejectFieldMapping,
              }
            : null
        }
      />

      <FinishedEditConfirmDialog dialog={finishedEditDialog} />
      <DeleteMappingSessionConfirm
        open={confirmAction === "DELETE"}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
        isDeleting={isDeletingSession}
        onConfirm={(removeSavedAnswers) => {
          setConfirmAction(null);
          void handleDeleteSession(removeSavedAnswers);
        }}
      />
      <ConfirmationModal
        open={confirmAction === "COMPLETE"}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
        title="Save all"
        eyebrow="Finalize product"
        description={`Nothing is left for you to decide. This saves the whole product as one job. The product leaves Need Review when the job succeeds. If it fails, the product stays and the job message shows why.${saveAllConfirmText(saveAllReady, saveAllHidden)}`}
        variant="primary"
        confirmLabel="Save all"
        onConfirm={() => {
          // Close the dialog as soon as the reviewer confirms — even when the
          // approval fails validation, so it cannot resurface with a stale
          // "0 selected" string after the bucket-clear.
          setConfirmAction(null);
          void completeReview();
        }}
      />

      <ApprovalPipelineActivityPanel
        jobs={activeApprovalPipelines}
        sidebarCollapsed={collapsed}
        hasStickyFooter={someSelected}
      />
    </DashboardLayout>
  );
}
